/* eslint-disable no-magic-numbers */
import {
  GeometryType,
  Model as TrbModel,
  PropertySet as TrbPropertySet,
  PropertySetDefinition,
  SinglePropertyDefinition,
  PropertyType,
} from '@technology/web3d-plugin-trimbim/dist/esnext/TrimBim_generated';
import { ByteBuffer } from 'flatbuffers';
import { UnreachableCaseError } from 'ts-essentials';
import { Vector3 } from '@technology/web3d';
import { getLogger } from '@trimble/logging';
import {
  TrajectoryMetadata,
  SectionChainageData,
  MarkupChainageData,
  SectionTag,
  SectionMaterialConfig,
  TrimbimAlignmentProperties,
  ChainageResult,
  AlignmentPosition,
} from './trimbim-alignment.types';
export type {
  TrajectoryMetadata,
  SectionChainageData,
  MarkupChainageData,
  SectionTag,
  SectionMaterialConfig,
  ChainageResult,
  AlignmentPosition,
};
import { parseChainageToMeters } from '@trimble/utilities';

const EPSILON = 1e-8;
const MAX_CHAINAGE_SEARCH_DISTANCE = 100; // in number of segments
const SEARCH_BACK = 20; // segments to look behind the current position
const ALIGNMENT_PROPERTIES_GROUP = 'AlignmentProperties';

// ---------------------------------------------------------------------------
// Station equation label parsing
// ---------------------------------------------------------------------------

/**
 * Parsed result of a station equation billboard label generated by the
 * Alignment API. The label text follows one of two patterns:
 *   - "Over length of {delta} from {start} to {end}"
 *   - "Gap of {delta} from {start} to {end}"
 *
 * `start` is the "back" chainage (approaching the equation point) and
 * `end` is the "ahead" chainage (leaving the equation point).
 */
export interface StationEquationInfo {
  type: 'gap' | 'overlength';
  /** Original delta substring as emitted by the API (kept verbatim). */
  deltaText: string;
  /** "Back" chainage in meters (approaching the equation point). */
  startMeters: number;
  /** "Ahead" chainage in meters (leaving the equation point). */
  endMeters: number;
}

const STATION_EQ_RE = /^(Over length|Gap) of (.+) from (.+) to (.+)$/i;

/**
 * Attempts to parse a billboard text as a station equation label.
 * Returns `undefined` if the text does not match the expected pattern.
 */
export function parseStationEquationLabel(
  text: string
): StationEquationInfo | undefined {
  const match = text.match(STATION_EQ_RE);
  if (!match) return undefined;

  const type = match[1].toLowerCase().startsWith('over') ? 'overlength' : 'gap';
  const deltaText = match[2];
  const startMeters = parseChainageToMeters(match[3]);
  const endMeters = parseChainageToMeters(match[4]);

  if (isNaN(startMeters) || isNaN(endMeters)) return undefined;

  return { type, deltaText, startMeters, endMeters };
}

// ---------------------------------------------------------------------------
// Cumulative 3D distance helpers
// ---------------------------------------------------------------------------

/**
 * Builds an array of cumulative 3D polyline distances at each segment vertex.
 * `result[0] = 0`, `result[i] = result[i-1] + length(segment[i-1])`.
 * Length = segments.length + 1 (one entry per vertex).
 */
export function buildSegmentWeb3dCumDists(segments: Segment[]): Float64Array {
  const web3dCumDists = new Float64Array(segments.length + 1);
  web3dCumDists[0] = 0;
  for (let i = 0; i < segments.length; i++) {
    const dx = segments[i].end.x - segments[i].start.x;
    const dy = segments[i].end.y - segments[i].start.y;
    const dz = segments[i].end.z - segments[i].start.z;
    web3dCumDists[i + 1] =
      web3dCumDists[i] + Math.sqrt(dx * dx + dy * dy + dz * dz);
  }
  return web3dCumDists;
}

/**
 * Converts a web3dChainage (segment index + fraction) to a cumulative 3D
 * distance along the polyline.
 */
export function web3dChainageToWeb3dCumDist(
  web3dChainage: number,
  segmentWeb3dCumDists: Float64Array
): number {
  const segCount = segmentWeb3dCumDists.length - 1;
  if (segCount === 0) return 0;

  const segIdx = Math.min(Math.floor(web3dChainage), segCount - 1);
  const frac = web3dChainage - segIdx;
  const segLen =
    segmentWeb3dCumDists[segIdx + 1] - segmentWeb3dCumDists[segIdx];
  return segmentWeb3dCumDists[segIdx] + frac * segLen;
}

/**
 * Converts a cumulative 3D distance back to a web3dChainage
 * (segment index + fraction) using binary search.
 */
export function web3dCumDistToWeb3dChainage(
  web3dCumDist: number,
  segmentWeb3dCumDists: Float64Array
): number {
  const segCount = segmentWeb3dCumDists.length - 1;
  if (segCount === 0) return 0;

  // Clamp to valid range
  if (web3dCumDist <= 0) return 0;
  if (web3dCumDist >= segmentWeb3dCumDists[segCount]) return segCount;

  // Binary search for the segment that contains this web3dCumDist
  let lo = 0;
  let hi = segCount;
  while (lo < hi - 1) {
    const mid = (lo + hi) >> 1;
    if (segmentWeb3dCumDists[mid] <= web3dCumDist) {
      lo = mid;
    } else {
      hi = mid;
    }
  }

  const segLen = segmentWeb3dCumDists[lo + 1] - segmentWeb3dCumDists[lo];
  const frac =
    segLen > EPSILON ? (web3dCumDist - segmentWeb3dCumDists[lo]) / segLen : 0;
  return lo + frac;
}

// ---------------------------------------------------------------------------
// Cumulative distance (adjusted for station equations)
// ---------------------------------------------------------------------------

/**
 * Computes `cumDistance` on each markup in-place.
 *
 * For the first markup, `cumDistance = 0`.
 * For subsequent markups:
 *   adjustment = (chainageBefore !== undefined) ? (chainageBefore - chainage) : 0
 *   cumDistance = prev.cumDistance + (relativeChainage - prev.relativeChainage) + adjustment
 *
 * - Gap  (chainageBefore < chainage): adjustment < 0 → reduces distance
 * - Overlength (chainageBefore > chainage): adjustment > 0 → adds distance
 */
export function computeCumDistances(markups: MarkupChainageData[]): void {
  if (markups.length === 0) return;
  markups[0].cumDistance = 0;
  for (let i = 1; i < markups.length; i++) {
    const m = markups[i];
    const prev = markups[i - 1];
    const relDiff = m.relativeChainage - prev.relativeChainage;
    const adjustment =
      m.chainageBefore !== undefined ? m.chainageBefore - m.chainage : 0;
    m.cumDistance = prev.cumDistance + relDiff + adjustment;
  }
}

// ---------------------------------------------------------------------------
// cumDistance ↔ chainage conversion (with overlength awareness)
// ---------------------------------------------------------------------------

/**
 * Converts a cumulative distance (relative to section start) to a chainage
 * and an overlength flag.
 *
 * Walks markup intervals using the `cumDistance` field. If the interpolated
 * chainage falls inside an overlength range [chainage, chainageBefore], the
 * result has `isInOverLength: true`.
 */
export function cumDistanceToChainage(
  distance: number,
  section: SectionChainageData
): ChainageResult | undefined {
  for (let i = 0; i < section.markups.length - 1; i++) {
    const lo = section.markups[i];
    const hi = section.markups[i + 1];
    if (distance >= lo.cumDistance && distance <= hi.cumDistance) {
      const span = hi.cumDistance - lo.cumDistance;
      const ratio = span > 0 ? (distance - lo.cumDistance) / span : 0;

      const loRel = lo.relativeChainage;
      const hiRel = effectiveHiRelChainage(hi, section.beginStation);
      const chainage = section.beginStation + loRel + ratio * (hiRel - loRel);

      const isInOverLength =
        hi.chainageBefore !== undefined &&
        hi.chainageBefore > hi.chainage &&
        chainage >= hi.chainage &&
        chainage <= hi.chainageBefore;

      return { chainage, isInOverLength };
    }
  }
  return undefined;
}

/**
 * Converts a chainage (with optional overlength flag) to a cumulative
 * distance relative to the section start.
 *
 * When `isInOverLength` is true and the chainage falls in a known overlength
 * range, the point is resolved from the pre-overlength interval (before the
 * markup). Otherwise it is resolved from the post-overlength interval.
 */
export function chainageToCumDistance(
  pos: ChainageResult,
  section: SectionChainageData
): number | undefined {
  const relCh = pos.chainage - section.beginStation;

  for (let i = 0; i < section.markups.length - 1; i++) {
    const lo = section.markups[i];
    const hi = section.markups[i + 1];
    const loRel = lo.relativeChainage;
    const hiRel = effectiveHiRelChainage(hi, section.beginStation);

    // Check if the chainage is in an overlength range at this boundary.
    // If isInOverLength is true, resolve from this (pre-overlength) interval.
    // If isInOverLength is false, skip and let the post-overlength interval
    // handle it.
    if (
      hi.chainageBefore !== undefined &&
      hi.chainageBefore > hi.chainage &&
      relCh >= hi.chainage - section.beginStation &&
      relCh <= hi.chainageBefore - section.beginStation
    ) {
      if (pos.isInOverLength) {
        const span = hiRel - loRel;
        const ratio = span > 0 ? (relCh - loRel) / span : 0;
        return lo.cumDistance + ratio * (hi.cumDistance - lo.cumDistance);
      }
      continue;
    }

    if (relCh >= loRel && relCh <= hiRel) {
      const span = hiRel - loRel;
      const ratio = span > 0 ? (relCh - loRel) / span : 0;
      return lo.cumDistance + ratio * (hi.cumDistance - lo.cumDistance);
    }
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// Chainage ↔ web3dChainage conversion (pure functions)
// ---------------------------------------------------------------------------

/**
 * Returns the effective upper-bound relativeChainage for an interval ending
 * at `next`. For station equation markups the "back" chainage defines the
 * approach side of the discontinuity; for regular markups the chainage itself
 * is used.
 */
function effectiveHiRelChainage(
  next: MarkupChainageData,
  beginStation: number
): number {
  return next.chainageBefore !== undefined
    ? next.chainageBefore - beginStation
    : next.relativeChainage;
}

/**
 * Converts a web3dChainage (segment index + fraction) to a chainage result
 * including whether the position is inside an overlength zone.
 *
 * Station equation markups (gaps / overlengths) are accounted for: the
 * interval approaching a station equation uses `chainageBefore` as upper
 * bound so the interpolation stays on the correct side of the discontinuity.
 *
 * Returns `undefined` when the web3dCumDist falls outside all markup intervals.
 */
export function web3dChainageToChainage(
  web3dChainage: number,
  section: SectionChainageData
): ChainageResult | undefined {
  const web3dCumDist = web3dChainageToWeb3dCumDist(
    web3dChainage,
    section.segmentWeb3dCumDists
  );

  for (let i = 0; i < section.markups.length - 1; i++) {
    const lo = section.markups[i].web3dCumDist;
    const hi = section.markups[i + 1].web3dCumDist;
    if (web3dCumDist >= lo && web3dCumDist <= hi) {
      const span = hi - lo;
      const ratio = span > 0 ? (web3dCumDist - lo) / span : 0;
      const loRel = section.markups[i].relativeChainage;
      const nextMarkup = section.markups[i + 1];
      const hiRel = effectiveHiRelChainage(nextMarkup, section.beginStation);
      const chainage = section.beginStation + loRel + ratio * (hiRel - loRel);

      // Clamp: if any gap markup exists at (or very near) the upper end
      // of this interval, ensure the result does not fall inside the gap.
      for (let j = i + 1; j < section.markups.length; j++) {
        if (section.markups[j].web3dCumDist > hi + EPSILON) break;
        const m = section.markups[j];
        if (
          m.chainageBefore !== undefined &&
          m.chainageBefore < m.chainage &&
          chainage > m.chainageBefore
        ) {
          return { chainage: m.chainageBefore, isInOverLength: false };
        }
      }

      // Check if we are inside an overlength zone (approaching the
      // overlength markup from the "before" side).
      const isInOverLength =
        nextMarkup.chainageBefore !== undefined &&
        nextMarkup.chainageBefore > nextMarkup.chainage &&
        chainage >= nextMarkup.chainage &&
        chainage <= nextMarkup.chainageBefore;

      return { chainage, isInOverLength };
    }
  }
  return undefined;
}

/**
 * Converts a chainage (with optional overlength flag) to a web3dChainage
 * (segment index + fraction) using cumulative 3D distance interpolation.
 *
 * Accepts either a plain number (backward compatible, `isInOverLength=false`)
 * or a `ChainageResult` with an explicit `isInOverLength` flag.
 *
 * Station equation markups are accounted for:
 * - Gaps: if the chainage falls inside a gap, navigation snaps to the gap end.
 * - Overlengths: if `isInOverLength` is true, resolves from the pre-overlap
 *   interval; otherwise from the post-overlap interval.
 *
 * Returns `undefined` when the chainage falls outside all markup intervals.
 */
export function chainageToWeb3dChainage(
  input: number | ChainageResult,
  section: SectionChainageData
): number | undefined {
  const chainage = typeof input === 'number' ? input : input.chainage;
  const isInOverLength =
    typeof input === 'number' ? false : (input.isInOverLength ?? false);

  const relativeChainage = chainage - section.beginStation;
  for (let i = 0; i < section.markups.length - 1; i++) {
    const next = section.markups[i + 1];
    const loRel = section.markups[i].relativeChainage;
    const hiRel = effectiveHiRelChainage(next, section.beginStation);

    // Overlength detection: if the chainage falls in the overlength range
    // and isInOverLength is true, resolve from this (pre-overlap) interval.
    if (
      next.chainageBefore !== undefined &&
      next.chainageBefore > next.chainage
    ) {
      const olStartRel = next.chainage - section.beginStation;
      const olEndRel = next.chainageBefore - section.beginStation;
      if (relativeChainage >= olStartRel && relativeChainage <= olEndRel) {
        if (isInOverLength) {
          const span = hiRel - loRel;
          const ratio = span > 0 ? (relativeChainage - loRel) / span : 0;
          const targetCumDist =
            section.markups[i].web3dCumDist +
            ratio * (next.web3dCumDist - section.markups[i].web3dCumDist);
          return web3dCumDistToWeb3dChainage(
            targetCumDist,
            section.segmentWeb3dCumDists
          );
        }
        continue;
      }
    }

    if (relativeChainage >= loRel && relativeChainage <= hiRel) {
      const span = hiRel - loRel;
      const ratio = span > 0 ? (relativeChainage - loRel) / span : 0;
      const targetCumDist =
        section.markups[i].web3dCumDist +
        ratio * (next.web3dCumDist - section.markups[i].web3dCumDist);
      return web3dCumDistToWeb3dChainage(
        targetCumDist,
        section.segmentWeb3dCumDists
      );
    }

    // Gap detection: the chainage falls inside the gap between
    // chainageBefore and chainage. Snap to the end of the gap.
    if (
      next.chainageBefore !== undefined &&
      next.chainageBefore < next.chainage
    ) {
      const gapStartRel = next.chainageBefore - section.beginStation;
      const gapEndRel = next.relativeChainage;
      if (relativeChainage > gapStartRel && relativeChainage < gapEndRel) {
        return web3dCumDistToWeb3dChainage(
          next.web3dCumDist,
          section.segmentWeb3dCumDists
        );
      }
    }
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// Reusable XY → web3dChainage mapping
// ---------------------------------------------------------------------------

interface XYPosition {
  x: number;
  y: number;
}

interface Segment {
  start: Vector3;
  end: Vector3;
}

/**
 * Core running-window projection: maps an ordered list of XY positions onto
 * the closest segment, returning one web3dChainage per position.
 *
 * @param positions  - XY positions in trajectory order.
 * @param segments   - Trajectory line segments (accumulated order).
 * @param searchBack - How many segments behind the current index to search.
 */
function projectPositionsOntoSegments(
  positions: XYPosition[],
  segments: Segment[],
  searchBack: number
): number[] {
  const result: number[] = [];
  let currentSegmentIndex = 0;

  for (const position of positions) {
    const searchStart = Math.max(0, currentSegmentIndex - searchBack);
    const searchEnd = Math.min(
      segments.length,
      currentSegmentIndex + MAX_CHAINAGE_SEARCH_DISTANCE
    );

    let web3dChainage = NaN;
    let bestDistanceSq = Infinity;

    for (let segIdx = searchStart; segIdx < searchEnd; segIdx++) {
      const segment = segments[segIdx];
      const startX = segment.start.x;
      const startY = segment.start.y;
      const endX = segment.end.x;
      const endY = segment.end.y;

      const segDirX = endX - startX;
      const segDirY = endY - startY;
      const segLengthSq = segDirX * segDirX + segDirY * segDirY;
      if (segLengthSq < EPSILON) continue;

      let projectionT =
        ((position.x - startX) * segDirX + (position.y - startY) * segDirY) /
        segLengthSq;
      projectionT = Math.max(0, Math.min(1, projectionT));
      const projectedX = startX + projectionT * segDirX;
      const projectedY = startY + projectionT * segDirY;
      const distanceSq =
        (position.x - projectedX) * (position.x - projectedX) +
        (position.y - projectedY) * (position.y - projectedY);

      if (distanceSq < bestDistanceSq) {
        bestDistanceSq = distanceSq;
        if (projectionT < 1) {
          web3dChainage = segIdx + projectionT;
        } else {
          web3dChainage =
            segIdx === segments.length - 1 ? segIdx + 1 : segIdx + 1 - EPSILON;
        }
      }
    }

    if (!isNaN(web3dChainage)) {
      currentSegmentIndex = Math.floor(web3dChainage);
    }

    result.push(web3dChainage);
  }

  return result;
}

/**
 * For a list of XY positions that are ordered along the trajectory, finds the
 * corresponding web3dChainage for each position by projecting it onto the
 * closest line segment in XY.
 *
 * The web3dChainage is a floating-point number where the integer part is the
 * segment index and the fractional part is the interpolation along that segment.
 *
 * Uses a running search window that advances forward, so positions MUST be
 * provided in trajectory order.
 *
 * @param positions - XY positions ordered along the trajectory.
 * @param segments - Line segments of the trajectory (in accumulated order).
 * @returns An array of web3dChainage values (one per position). `NaN` if no
 *          matching segment was found for a position.
 */
export function findWeb3dChainagesForPositions(
  positions: XYPosition[],
  segments: Segment[]
): number[] {
  return projectPositionsOntoSegments(positions, segments, SEARCH_BACK);
}

/**
 * Projects billboard XY positions onto segments using a running-window
 * search. Billboards must be pre-sorted by relativeChainage.
 *
 * For each billboard, the function looks ahead in the list for upcoming
 * overlength station equations. When one is found, the forward search
 * distance is extended to cover the overlength's physical position
 * (which is at `chainageBefore`, further along the trajectory than the
 * overlength's chainage sort position).
 */
function findWeb3dChainagesWithOverlengthAwareness(
  billboards: Array<{
    worldX: number;
    worldY: number;
    relativeChainage: number;
    chainageBefore?: number;
    chainage: number;
  }>,
  segments: Segment[]
): number[] {
  let maxOverlengthRange = 0;
  for (const bb of billboards) {
    if (bb.chainageBefore !== undefined && bb.chainageBefore > bb.chainage) {
      maxOverlengthRange = Math.max(
        maxOverlengthRange,
        bb.chainageBefore - bb.chainage
      );
    }
  }
  const searchBack = Math.max(SEARCH_BACK, Math.ceil(maxOverlengthRange * 1.5));

  const positions: XYPosition[] = billboards.map((bb) => ({
    x: bb.worldX,
    y: bb.worldY,
  }));

  return projectPositionsOntoSegments(positions, segments, searchBack);
}

// ---------------------------------------------------------------------------
// Position + perpendicular from web3dChainage
// ---------------------------------------------------------------------------

/**
 * Computes the trajectory surface Z and the XY-perpendicular direction at a
 * given web3dChainage.  Uses world-space segments produced during analysis.
 */
function computePositionAndPerpendicular(
  web3dChainage: number,
  segments: Segment[],
  worldX: number,
  worldY: number
): { worldPosition: Vector3; perpendicularVector: Vector3 } {
  const segIdx = Math.min(
    Math.max(Math.floor(web3dChainage), 0),
    segments.length - 1
  );
  const frac = web3dChainage - segIdx;

  const z =
    segments[segIdx].start.z +
    frac * (segments[segIdx].end.z - segments[segIdx].start.z);

  const segDirX = segments[segIdx].end.x - segments[segIdx].start.x;
  const segDirY = segments[segIdx].end.y - segments[segIdx].start.y;
  const len = Math.sqrt(segDirX * segDirX + segDirY * segDirY);

  return {
    worldPosition: new Vector3(worldX, worldY, z),
    perpendicularVector:
      len > EPSILON
        ? new Vector3(segDirY / len, -segDirX / len, 0)
        : new Vector3(0, 0, 0),
  };
}

// ---------------------------------------------------------------------------
// Alignment property extraction
// ---------------------------------------------------------------------------

const KNOWN_ALIGNMENT_PROPERTIES = [
  'type',
  'beginStation',
  'endStation',
  'name',
  'tag',
] as const;
type AlignmentPropertyName = (typeof KNOWN_ALIGNMENT_PROPERTIES)[number];

function isKnownAlignmentProperty(name: string): name is AlignmentPropertyName {
  return (KNOWN_ALIGNMENT_PROPERTIES as readonly string[]).includes(name);
}

function toCamelCase(str: string): string {
  return `${str[0]?.toLowerCase() ?? ''}${str.slice(1)}`;
}

/**
 * Convert a property value to meters.
 * If it is already a number it is assumed to be in meters.
 * If it is a string with a unit suffix (e.g. "1234.56 ft") the value is
 * parsed and converted to meters via {@link parseChainageToMeters}.
 */
function toMeters(value: string | number | bigint): string | number {
  if (typeof value === 'number') return value;
  const str = value.toString();
  const meters = parseChainageToMeters(str);
  return isNaN(meters) ? str : meters;
}

/**
 * Extracts and decodes property-sets for the Trimbim FlatBuffers model.
 *
 * NOTE: We intentionally mirror the Web3D `QueryManager` decoding logic so that
 * the results match `TrimbimModel.getProperties(...)` / `AlignmentNavigationTool`.
 */
function extractTrimbimAlignmentProperties(
  flatModel: TrbModel
): TrimbimAlignmentProperties[] {
  const entities = flatModel.entities();
  const properties = flatModel.properties();
  if (!entities || !properties) {
    return [];
  }

  const entitiesLength = entities.entitiesLength() || 0;
  const propertySetBindingsLength = properties.propertySetBindingsLength() || 0;

  // Build entity -> propertySetBindingId index (mirrors QueryManager.loadPropertyIndex)
  const indexByEntityId = new Array<Set<number>>(entitiesLength);
  for (let i = 0; i < entitiesLength; i++) {
    indexByEntityId[i] = new Set<number>();
  }
  const binding = new TrbPropertySet();
  for (let bindingId = 0; bindingId < propertySetBindingsLength; bindingId++) {
    properties.propertySetBindings(bindingId, binding);
    for (let j = 0, l = binding.entitiesLength(); j < l; j++) {
      const entityId = binding.entities(j);
      if (entityId === null || entityId === undefined) continue;
      indexByEntityId[entityId]?.add(bindingId);
    }
  }

  const def = new PropertySetDefinition();
  const singleDef = new SinglePropertyDefinition();

  const getValue = (
    type: PropertyType,
    valueIndex: number
  ): string | number | bigint => {
    switch (type) {
      case PropertyType.LengthMeasure:
        return properties.lengthMeasures(valueIndex) ?? 0;
      case PropertyType.AreaMeasure:
        return properties.areaMeasures(valueIndex) ?? 0;
      case PropertyType.VolumeMeasure:
        return properties.volumeMeasures(valueIndex) ?? 0;
      case PropertyType.MassMeasure:
        return properties.massMeasures(valueIndex) ?? 0;
      case PropertyType.AngleMeasure:
        return properties.angleMeasures(valueIndex) ?? 0;
      case PropertyType.StringValue:
        return properties.stringValues(valueIndex) ?? '';
      case PropertyType.IntValue:
        // convert uint32 to int32 (mirrors QueryManager)
        return valueIndex & 0x80000000 ? valueIndex - 0x100000000 : valueIndex;
      case PropertyType.DoubleValue:
        return properties.doubleValues(valueIndex) ?? 0;
      case PropertyType.DateTime:
        return properties.dateTimeValues(valueIndex) ?? BigInt(0);
      case PropertyType.Logical:
      case PropertyType.Boolean:
        return valueIndex;
      default:
        throw new UnreachableCaseError(type);
    }
  };

  const results: TrimbimAlignmentProperties[] = [];

  for (let entityId = 0; entityId < entitiesLength; entityId++) {
    const bindingIds = indexByEntityId[entityId];
    if (!bindingIds || bindingIds.size === 0) continue;

    // Find the first AlignmentProperties property set for this entity (matches tool behavior)
    for (const bindingId of bindingIds) {
      const b = properties.propertySetBindings(bindingId, binding);
      if (!b) continue;
      const d = properties.definitions(b.definition(), def);
      if (!d) continue;

      const propertySetName = properties.propertySetNames(d.name());
      if (propertySetName !== ALIGNMENT_PROPERTIES_GROUP) continue;

      const decoded: TrimbimAlignmentProperties = { entityId };

      for (let i = 0, l = d.propertiesLength(); i < l; i++) {
        const spd = d.properties(i, singleDef);
        if (!spd) continue;
        const rawName = properties.propertyNames(spd.name());
        if (!rawName) continue;

        const propertyName = toCamelCase(rawName);
        if (!isKnownAlignmentProperty(propertyName)) {
          continue;
        }

        const valueIndex = b.values(i) ?? 0;
        const value = getValue(spd.type(), valueIndex);

        switch (propertyName) {
          case 'type':
            decoded.type = value.toString().toLowerCase();
            break;
          case 'beginStation':
            decoded.beginStation = toMeters(value);
            break;
          case 'endStation':
            decoded.endStation = toMeters(value);
            break;
          case 'name':
            decoded.name = value as string;
            break;
          case 'tag':
            decoded.tag = value as string;
            break;
          default:
            throw new UnreachableCaseError(propertyName);
        }
      }

      results.push(decoded);
      break;
    }
  }

  return results;
}

async function getGeometryInstancesByEntityId(
  flatModel: TrbModel
): Promise<Map<number, number[]>> {
  const geometryInstancesByEntityId: Map<number, number[]> = new Map();
  const geometry = flatModel.geometry();
  if (!geometry) {
    return new Map();
  }

  for (
    let instanceIndex = 0;
    instanceIndex < (geometry?.instancesLength() || 0);
    instanceIndex++
  ) {
    const instance = geometry.instances(instanceIndex);
    if (instance) {
      const entityId: number = instance.entityId();
      const instances = geometryInstancesByEntityId.get(entityId) || [];
      instances.push(instanceIndex);
      geometryInstancesByEntityId.set(entityId, instances);
    }
  }
  return geometryInstancesByEntityId;
}

/**
 * Finds entity ids that have at least one instance whose definition matches the given geometry type.
 * Iterates entities in id order to match Web3D's `findEntitiesByGeometryType` behavior as closely as possible.
 */
function findEntitiesByGeometryType(
  flatModel: TrbModel,
  geometryInstancesByEntityId: Map<number, number[]>,
  geometryType: GeometryType
): number[] {
  const geometry = flatModel.geometry();
  if (!geometry) {
    return [];
  }

  const entitiesLength = flatModel.entities()?.entitiesLength() ?? 0;
  const result: number[] = [];
  for (let entityId = 0; entityId < entitiesLength; entityId++) {
    const instances = geometryInstancesByEntityId.get(entityId);
    if (!instances || instances.length === 0) continue;

    for (const instanceIndex of instances) {
      const instance = geometry.instances(instanceIndex);
      if (!instance) continue;
      const definition = geometry.definitions(instance.definitionId());
      if (!definition) continue;
      if (definition.type() === geometryType) {
        result.push(entityId);
        break;
      }
    }
  }
  return result;
}

/**
 * Mirrors `AlignmentNavigationTool.getNavigableAlignmentEntities(...)` without using TrimbimModel.
 *
 * Algorithm:
 * - If there are no `AlignmentProperties` anywhere, return [].
 * - Take all `GraphicsContainer` entities and filter out those with type left/right-rail.
 */
function getNavigableAlignmentEntitiesFromFlatModel(
  flatModel: TrbModel,
  geometryInstancesByEntityId: Map<number, number[]>,
  alignmentProperties: TrimbimAlignmentProperties[]
): number[] {
  if (alignmentProperties.length === 0) {
    return [];
  }

  const propertiesByEntityId = new Map<number, TrimbimAlignmentProperties>();
  for (const p of alignmentProperties) {
    propertiesByEntityId.set(p.entityId, p);
  }

  const graphicsContainerEntities = findEntitiesByGeometryType(
    flatModel,
    geometryInstancesByEntityId,
    GeometryType.GraphicsContainer
  );

  return graphicsContainerEntities.filter((entityId) => {
    const type = propertiesByEntityId.get(entityId)?.type;
    return !['left-rail', 'right-rail'].includes(type ?? '');
  });
}

export async function analyzeTrimbimAlignment(
  metadata: TrajectoryMetadata,
  trimbim: ArrayBuffer
): Promise<TrajectoryMetadata> {
  const startTime = performance.now();

  const flatModel = TrbModel.getRootAsModel(
    new ByteBuffer(new Uint8Array(trimbim))
  );

  // Extract & cache decoded Trimbim properties (avoid re-decoding on future analyses)
  const alignmentProperties =
    metadata.trimbimProperties?.alignmentProperties ??
    extractTrimbimAlignmentProperties(flatModel);

  getLogger().debug(
    'AlignmentProperties from trimbim:',
    JSON.stringify(alignmentProperties, null, 2)
  );

  const geometryInstancesByEntityId =
    await getGeometryInstancesByEntityId(flatModel);

  const sectionChainageDatas: SectionChainageData[] = [];
  const navigableEntities = getNavigableAlignmentEntitiesFromFlatModel(
    flatModel,
    geometryInstancesByEntityId,
    alignmentProperties
  );

  for (const entity of navigableEntities) {
    getLogger().debug(
      `extracting chainage data for entity ${entity} - tag: ${metadata.sectionTags[sectionChainageDatas.length].tag}`
    );
    const currentSectionIndex = sectionChainageDatas.length;
    // the order of the navigable entities is matching the order of the section tags
    const sectionTag = metadata.sectionTags[currentSectionIndex];

    const instances = geometryInstancesByEntityId.get(entity);
    if (!instances || instances.length === 0) {
      continue;
    }

    const { markups, segmentWeb3dCumDists } = await extractMarkupChainages(
      flatModel,
      entity,
      instances,
      sectionTag
    );

    const sectionLength =
      markups.length > 0 ? markups[markups.length - 1].cumDistance : 0;
    const expectedLength =
      (sectionTag.endStation || 0) - (sectionTag.beginStation || 0);

    if (Math.abs(sectionLength - expectedLength) > EPSILON) {
      getLogger().warn(
        `Section "${sectionTag.tag ?? entity}": computed length ${sectionLength.toFixed(3)} !== ` +
          `endStation - beginStation ${expectedLength.toFixed(3)} ` +
          `(delta=${(sectionLength - expectedLength).toFixed(3)})`
      );
    }

    sectionChainageDatas.push({
      entityId: entity,
      beginStation: sectionTag.beginStation || 0,
      endStation: sectionTag.endStation || 0,
      markups,
      segmentWeb3dCumDists,
      length: sectionLength,
      beginDistance: 0,
      endDistance: 0,
    });
  }

  // Compute global beginDistance / endDistance across sections.
  let currentDistance = metadata.beginStation;
  let hasStationEquations = false;
  for (const section of sectionChainageDatas) {
    section.beginDistance = currentDistance;
    section.endDistance = currentDistance + section.length;
    currentDistance = section.endDistance;
    if (section.markups.some((m) => m.chainageBefore !== undefined)) {
      hasStationEquations = true;
    }
  }

  const endDistance =
    sectionChainageDatas.length > 0
      ? sectionChainageDatas[sectionChainageDatas.length - 1].endDistance
      : metadata.beginStation;

  if (Math.abs(endDistance - metadata.endStation) > EPSILON) {
    const logFn = hasStationEquations
      ? getLogger().warn.bind(getLogger())
      : getLogger().error.bind(getLogger());
    logFn(
      `Alignment endDistance ${endDistance.toFixed(3)} !== ` +
        `endStation ${metadata.endStation.toFixed(3)} ` +
        `(delta=${(endDistance - metadata.endStation).toFixed(3)}, ` +
        `hasStationEquations=${hasStationEquations})`
    );
  }

  const result: TrajectoryMetadata = {
    ...metadata,
    trimbimProperties: { ...metadata.trimbimProperties, alignmentProperties },
    sectionChainages: sectionChainageDatas,
    beginDistance: metadata.beginStation,
    endDistance,
  };
  const endTime = performance.now();
  getLogger().info(
    `analyzeTrimbimAlignment took ${(endTime - startTime).toFixed(2)} ms`
  );
  return result;
}

const extractMarkupChainages = async (
  flatModel: TrbModel,
  entity: number,
  instances: number[],
  sectionTag: { beginStation: number; endStation: number }
): Promise<{
  lineSegmentsCount: number;
  markups: MarkupChainageData[];
  segmentWeb3dCumDists: Float64Array;
}> => {
  const geometry = flatModel.geometry()!;
  let zeroLengthSegmentsCount = 0;

  const ent = flatModel.entities()?.entities(entity);
  const globalPlacement = ent?.globalPlacement();
  const globalOrigin = { x: 0, y: 0, z: 0 };
  if (globalPlacement) {
    globalOrigin.x = globalPlacement.origin()?.x() ?? 0;
    globalOrigin.y = globalPlacement.origin()?.y() ?? 0;
    globalOrigin.z = globalPlacement.origin()?.z() ?? 0;
  }

  // ── Pass 1: Collect all line segments and billboard positions ──────
  const segments: { start: Vector3; end: Vector3 }[] = [];
  const billboardData: Array<{
    chainage: number;
    relativeChainage: number;
    worldX: number;
    worldY: number;
    text: string;
    /** Defined only for station equation markups ("back" chainage in meters). */
    chainageBefore?: number;
  }> = [];

  for (const instanceIndex of instances) {
    const instance = geometry.instances(instanceIndex);
    if (!instance) continue;

    const localPlacementId = instance.localPlacementId();
    const localPlacement = geometry.localPlacements(localPlacementId);
    const origin = { x: 0, y: 0, z: 0 };
    if (localPlacement) {
      origin.x = localPlacement.origin()?.x() ?? 0;
      origin.y = localPlacement.origin()?.y() ?? 0;
      origin.z = localPlacement.origin()?.z() ?? 0;
    }
    const definition = geometry.definitions(instance.definitionId());
    if (!definition) continue;

    switch (definition.type()) {
      case GeometryType.GraphicsContainer: {
        const graphicsContainer = geometry.graphicsContainers(
          definition.index()
        );
        for (
          let i = 0;
          i < (graphicsContainer?.lineSegmentsLength() ?? 0);
          i++
        ) {
          const lineSegment = graphicsContainer?.lineSegments(i);
          if (lineSegment) {
            const start = lineSegment.start();
            const end = lineSegment.end();
            if (start && end) {
              const worldStart = new Vector3(
                globalOrigin.x + origin.x + start.x(),
                globalOrigin.y + origin.y + start.y(),
                globalOrigin.z + origin.z + start.z()
              );
              const worldEnd = new Vector3(
                globalOrigin.x + origin.x + end.x(),
                globalOrigin.y + origin.y + end.y(),
                globalOrigin.z + origin.z + end.z()
              );
              segments.push({ start: worldStart, end: worldEnd });
              if (worldStart.distanceToSquared(worldEnd) < EPSILON) {
                zeroLengthSegmentsCount++;
              }
            }
          }
        }
        getLogger().debug(
          `Found line segments (${segments.length}), total count now`,
          segments.length,
          ', zero-length segments:',
          zeroLengthSegmentsCount
        );
        break;
      }

      case GeometryType.BillboardText: {
        const billboardText = geometry.billboardTexts(definition.index());
        const text = billboardText?.text() || '';
        try {
          const chainage = parseChainageToMeters(text);
          if (!isNaN(chainage)) {
            const relativeChainage = chainage - sectionTag.beginStation;
            if (relativeChainage >= 0) {
              billboardData.push({
                chainage,
                relativeChainage,
                worldX: globalOrigin.x + origin.x,
                worldY: globalOrigin.y + origin.y,
                text,
              });
            }
          } else {
            const stationEq = parseStationEquationLabel(text);
            if (stationEq) {
              getLogger().debug(
                `Station equation ${stationEq.type} detected: "${text}"`
              );

              // For gaps: chainage = end (ahead), chainageBefore = start (back)
              //   → chainageBefore < chainage
              // For overlengths: chainage = start (ahead), chainageBefore = end (back)
              //   → chainageBefore > chainage
              // The delta can be negative (e.g. "Over length of -4.735m …"),
              // so we cannot rely on start > end for overlengths.
              const isOverlength = stationEq.type === 'overlength';
              const aheadChainage = isOverlength
                ? stationEq.startMeters
                : stationEq.endMeters;
              const backChainage = isOverlength
                ? stationEq.endMeters
                : stationEq.startMeters;
              const relativeChainage = aheadChainage - sectionTag.beginStation;

              billboardData.push({
                chainage: aheadChainage,
                relativeChainage,
                worldX: globalOrigin.x + origin.x,
                worldY: globalOrigin.y + origin.y,
                text,
                chainageBefore: backChainage,
              });
            }
          }
        } catch (error) {
          getLogger().error('Error parsing chainage:', error);
        }
        break;
      }
      default:
        break;
    }
  }

  // ── Pass 2: Build cumulative 3D distance array ─────────────────
  const segmentWeb3dCumDists = buildSegmentWeb3dCumDists(segments);
  const totalDist = segmentWeb3dCumDists[segments.length] || 0;

  // ── Pass 2b: Sort billboards by relativeChainage ────────────────
  // Ensures the running-window XY projection processes billboards in
  // approximate trajectory order. At the same relativeChainage, station
  // equation markups come first so effectiveHiRelChainage limits the
  // preceding interval correctly.
  billboardData.sort((a, b) => {
    const diff = a.relativeChainage - b.relativeChainage;
    if (diff !== 0) return diff;
    const aIsEq = a.chainageBefore !== undefined ? 0 : 1;
    const bIsEq = b.chainageBefore !== undefined ? 0 : 1;
    return aIsEq - bIsEq;
  });

  // ── Pass 3: Map billboard XY positions to web3dChainages ──────────
  // Uses a running-window search. Overlength station equations have XY
  // positions ahead of their chainage sort position (at chainageBefore),
  // so the window is extended forward by scanning ahead for overlengths.
  const web3dChainages = findWeb3dChainagesWithOverlengthAwareness(
    billboardData,
    segments
  );

  // Sort billboardData + web3dChainages by web3dChainage so markups
  // follow physical trajectory order.
  const indices = billboardData.map((_, i) => i);
  indices.sort((a, b) => {
    const wa = web3dChainages[a];
    const wb = web3dChainages[b];
    if (isNaN(wa) && isNaN(wb)) return 0;
    if (isNaN(wa)) return 1;
    if (isNaN(wb)) return -1;
    const diff = wa - wb;
    if (Math.abs(diff) > EPSILON) return diff;
    const aIsEq = billboardData[a].chainageBefore !== undefined ? 0 : 1;
    const bIsEq = billboardData[b].chainageBefore !== undefined ? 0 : 1;
    return aIsEq - bIsEq;
  });
  const sortedBillboards = indices.map((i) => billboardData[i]);
  const sortedWeb3dChainages = indices.map((i) => web3dChainages[i]);
  billboardData.length = 0;
  billboardData.push(...sortedBillboards);
  web3dChainages.length = 0;
  web3dChainages.push(...sortedWeb3dChainages);

  // ── Build markups array ───────────────────────────────────────────
  const markups: MarkupChainageData[] = [];

  // Check whether the first billboard is at the beginStation (relativeChainage ≈ 0).
  // If not, insert a virtual beginStation anchor without a label.
  const hasBeginBillboard =
    billboardData.length > 0 && billboardData[0].relativeChainage < EPSILON;

  if (!hasBeginBillboard) {
    const { worldPosition, perpendicularVector } =
      computePositionAndPerpendicular(
        0,
        segments,
        segments[0]?.start.x ?? 0,
        segments[0]?.start.y ?? 0
      );
    markups.push({
      chainage: sectionTag.beginStation || 0,
      relativeChainage: 0,
      web3dChainage: 0,
      web3dCumDist: 0,
      cumDistance: 0,
      worldPosition,
      perpendicularVector,
    });
  }

  for (let i = 0; i < billboardData.length; i++) {
    const billboard = billboardData[i];
    const web3dChainage = web3dChainages[i];

    if (isNaN(web3dChainage)) {
      getLogger().warn(
        `Could not find web3d chainage for markup at position (${billboard.worldX}, ${billboard.worldY}) with real chainage ${billboard.chainage} relative chainage ${billboard.relativeChainage}`
      );
    }

    const web3dCumDist = isNaN(web3dChainage)
      ? NaN
      : web3dChainageToWeb3dCumDist(web3dChainage, segmentWeb3dCumDists);

    const posPerp = isNaN(web3dChainage)
      ? undefined
      : computePositionAndPerpendicular(
          web3dChainage,
          segments,
          billboard.worldX,
          billboard.worldY
        );

    markups.push({
      chainage: billboard.chainage,
      relativeChainage: billboard.relativeChainage,
      web3dChainage,
      web3dCumDist,
      cumDistance: 0,
      worldPosition: posPerp?.worldPosition,
      label: billboard.text,
      perpendicularVector: posPerp?.perpendicularVector,
      chainageBefore: billboard.chainageBefore,
    });
  }

  // endStation markup: update the last entry if it coincides with the polyline end,
  // otherwise append a virtual anchor without a label.
  const endRelChainage =
    (sectionTag.endStation || 0) - (sectionTag.beginStation || 0);
  const lastSeg = segments.length > 0 ? segments[segments.length - 1] : null;

  if (
    markups.length > 0 &&
    Math.abs(markups[markups.length - 1].web3dChainage - segments.length) <
      EPSILON
  ) {
    const prev = markups[markups.length - 1];
    markups[markups.length - 1] = {
      ...prev,
      chainage: sectionTag.endStation || 0,
      relativeChainage: endRelChainage,
      web3dChainage: segments.length,
      web3dCumDist: totalDist,
    };
  } else {
    const endX = lastSeg?.end.x ?? 0;
    const endY = lastSeg?.end.y ?? 0;
    const { worldPosition, perpendicularVector } =
      computePositionAndPerpendicular(segments.length, segments, endX, endY);
    markups.push({
      chainage: sectionTag.endStation || 0,
      relativeChainage: endRelChainage,
      web3dChainage: segments.length,
      web3dCumDist: totalDist,
      cumDistance: 0,
      worldPosition,
      perpendicularVector,
    });
  }

  // ── Pass 5: Compute cumDistance on each markup ─────────────────────
  computeCumDistances(markups);

  return {
    lineSegmentsCount: segments.length,
    markups,
    segmentWeb3dCumDists,
  };
};

export async function analyzeTrimbimModel(
  modelId: string,
  trimbim: ArrayBuffer
): Promise<TrajectoryMetadata> {
  const flatModel = TrbModel.getRootAsModel(
    new ByteBuffer(new Uint8Array(trimbim))
  );

  const geometryInstancesByEntityId =
    await getGeometryInstancesByEntityId(flatModel);

  const sectionTags: SectionTag[] = [];
  const linesPerEntity: Map<number, number> = new Map();

  // Process each selected entity within this model
  for (const entity of geometryInstancesByEntityId.keys()) {
    const instances = geometryInstancesByEntityId.get(entity);
    if (!instances || instances.length === 0) {
      continue;
    }

    const { lineSegmentsCount } = await extractWeb3DChainage(
      flatModel,
      instances
    );
    if (lineSegmentsCount > 0) {
      sectionTags.push({
        tag: `Section ${entity}`,
        beginStation: 0,
        endStation: lineSegmentsCount,
      });

      linesPerEntity.set(entity, lineSegmentsCount);
    }
  }
  return {
    modelId: modelId,
    sectionTags: sectionTags,
    isAlignment: false,
    beginStation: 0,
    endStation: 0,
    beginDistance: 0,
    endDistance: 0,
    linesPerEntity,
  };
}

async function extractWeb3DChainage(
  flatModel: TrbModel,
  instances: number[]
): Promise<{
  lineSegmentsCount: number;
}> {
  const geometry = flatModel.geometry()!;

  let lineSegmentsCount = 0;

  // Process instances for the current entity
  for (const instanceIndex of instances) {
    const instance = geometry.instances(instanceIndex);
    if (!instance) continue;

    const definition = geometry.definitions(instance.definitionId());
    if (!definition) continue;

    // we extract positions of alignment markups in the trimbim model ("web3d chainage" - integer part is id of line segment, decimal part is relative position along the segment)
    // in web3d the chainage is relative to the current section (one entity for each section)

    switch (definition.type()) {
      case GeometryType.GraphicsContainer:
        {
          const lineSegments = geometry.graphicsContainers(definition.index());
          const lineSegmentsLength = lineSegments?.lineSegmentsLength() ?? 0;
          lineSegmentsCount += lineSegmentsLength;
        }
        break;
      default:
        break;
    }
  }

  return { lineSegmentsCount };
}
